import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Chip, TextField, IconButton, FormControl, InputLabel, Select, MenuItem,
    Checkbox, Tooltip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { PayrollService } from '../../services/payroll.service';
import { IPayroll } from '../../interfaces/payroll.interface';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import { displayUsername } from '../../utils/userUtils';
import { UserType } from '../../interfaces/userType.enum';
import { useTranslation } from 'react-i18next';
import BatchPaymentDialog, { BatchPayrollRow } from './BatchPaymentDialog';

type MonthNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
const MONTH_NUMBERS: MonthNumber[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const PayrollList: React.FC = () => {
    const { t } = useTranslation(['payroll', 'common']);
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [payrolls, setPayrolls] = useState<IPayroll[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [batchDialogOpen, setBatchDialogOpen] = useState(false);
    const { showSuccess, showApiError } = useToast();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();

    const userRoles = user && user.roles ? (Array.isArray(user.roles) ? user.roles : [user.roles]) : [];
    const isInstitutionAdmin = userRoles.includes(UserType.InstitutionAdmin);
    const canCreatePayment = usePermission('PAYROLL_PAYMENT_CREATE', isInstitutionAdmin);

    const loadPayrolls = async () => {
        if (!institutionId) return;
        try {
            const data = await PayrollService.getPayrollList(year, month, 0, 50, institutionId);
            setPayrolls(data.content);
            setLoaded(true);
            setSelectedIds(new Set());
        } catch (error) {
            showApiError(error, t('payrollList.toast.loadFailed'));
        }
    };

    const handleGenerate = async () => {
        try {
            const generated = await PayrollService.generatePayroll(year, month, institutionId);
            showSuccess(t('payrollList.toast.generated', { count: generated.length }));
            loadPayrolls();
        } catch (error) {
            showApiError(error, t('payrollList.toast.generateFailed'));
        }
    };

    const handleFinalizeAll = async () => {
        try {
            await PayrollService.finalizeAllPayroll(year, month, institutionId);
            showSuccess(t('payrollList.toast.allFinalized'));
            loadPayrolls();
        } catch (error) {
            showApiError(error, t('payrollList.toast.finalizeAllFailed'));
        }
    };

    const handleFinalize = async (id: string) => {
        try {
            await PayrollService.finalizePayroll(id);
            showSuccess(t('payrollList.toast.finalized'));
            loadPayrolls();
        } catch (error) {
            showApiError(error, t('payrollList.toast.finalizeFailed'));
        }
    };

    const handleDownload = async (id: string) => {
        try {
            await PayrollService.downloadPayslip(id);
        } catch (error) {
            showApiError(error, t('shared.downloadPayslipFailed'));
        }
    };

    const handleToggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const finalizedIds = payrolls.filter(p => p.status === 'FINALIZED').map(p => p.id);
    const allFinalizedSelected = finalizedIds.length > 0 && finalizedIds.every(id => selectedIds.has(id));
    const someFinalizedSelected = finalizedIds.some(id => selectedIds.has(id)) && !allFinalizedSelected;

    const handleToggleSelectAll = () => {
        if (allFinalizedSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(finalizedIds));
        }
    };

    const selectedBatchRows: BatchPayrollRow[] = payrolls
        .filter(p => selectedIds.has(p.id))
        .map(p => ({
            id: p.id,
            employeeName: displayUsername(p.userName),
            netSalary: p.netSalary,
            // NOTE: banking fields are not currently exposed on PayrollDTO;
            // preview will degrade gracefully (see BatchPaymentDialog).
            user: undefined,
        }));

    const handleBatchSuccess = () => {
        setSelectedIds(new Set());
        loadPayrolls();
    };

    // Clear selections whenever any filter changes so stale selections don't
    // survive into a different year/month/institution context.
    const handleYearChange = (val: number) => {
        setSelectedIds(new Set());
        setYear(val);
    };
    const handleMonthChange = (val: number) => {
        setSelectedIds(new Set());
        setMonth(val);
    };
    const handleInstitutionChange = (val: string) => {
        setSelectedIds(new Set());
        setInstitutionId(val);
    };

    const hasDrafts = payrolls.some(p => p.status === 'DRAFT');

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>{t('payrollList.title')}</Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                {isSystemAdmin && (
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>{t('shared.selectInstitution')}</InputLabel>
                        <Select
                            value={institutionId}
                            onChange={(e) => handleInstitutionChange(e.target.value)}
                            label={t('shared.selectInstitution')}
                        >
                            {institutions.map((inst) => (
                                <MenuItem key={inst.id} value={inst.id}>{inst.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                <TextField
                    label={t('common:fields.year')}
                    type="number"
                    value={year}
                    onChange={(e) => handleYearChange(parseInt(e.target.value))}
                    size="small"
                    sx={{ width: 120 }}
                />
                <TextField
                    label={t('common:fields.month')}
                    select
                    value={month}
                    onChange={(e) => handleMonthChange(parseInt(e.target.value))}
                    size="small"
                    sx={{ width: 150 }}
                    SelectProps={{ native: true }}
                >
                    {MONTH_NUMBERS.map(m => (
                        <option key={m} value={m}>{t(`common:months.${m}`)}</option>
                    ))}
                </TextField>
                <Button variant="outlined" onClick={loadPayrolls} disabled={!institutionId}>{t('shared.load')}</Button>
                <Button variant="contained" color="primary" onClick={handleGenerate} disabled={!institutionId}>
                    {t('payrollList.generate')}
                </Button>
                {hasDrafts && (
                    <Button variant="contained" color="warning" onClick={handleFinalizeAll}>
                        {t('payrollList.finalizeAll')}
                    </Button>
                )}
                <Button
                    variant="outlined"
                    onClick={() => navigate('/payroll/reconciliation')}
                    disabled={!institutionId}
                >
                    {t('payrollList.reconciliation')}
                </Button>
                {canCreatePayment && selectedIds.size > 0 && (
                    <Button
                        variant="contained"
                        color="success"
                        onClick={() => setBatchDialogOpen(true)}
                    >
                        {t('payrollList.markPaidBulk', { count: selectedIds.size })}
                    </Button>
                )}
            </Box>

            {loaded && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={someFinalizedSelected}
                                        checked={allFinalizedSelected}
                                        onChange={handleToggleSelectAll}
                                        disabled={finalizedIds.length === 0}
                                        inputProps={{ 'aria-label': t('payrollList.selectAllFinalized') }}
                                    />
                                </TableCell>
                                <TableCell>{t('shared.employee')}</TableCell>
                                <TableCell align="right">{t('payrollList.columns.gross')}</TableCell>
                                <TableCell align="right">{t('payrollList.columns.deductions')}</TableCell>
                                <TableCell align="right">{t('payrollList.columns.leaveDed')}</TableCell>
                                <TableCell align="right">{t('payrollList.columns.absenceDed')}</TableCell>
                                <TableCell align="right">{t('shared.netSalary')}</TableCell>
                                <TableCell>{t('common:fields.status')}</TableCell>
                                <TableCell align="right">{t('common:fields.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {payrolls.map((p) => {
                                const selectable = p.status === 'FINALIZED';
                                return (
                                    <TableRow key={p.id}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedIds.has(p.id)}
                                                onChange={() => handleToggleSelect(p.id)}
                                                disabled={!selectable}
                                                inputProps={{ 'aria-label': t('payrollList.selectPayroll', { id: p.id }) }}
                                            />
                                        </TableCell>
                                        <TableCell>{displayUsername(p.userName)}</TableCell>
                                        <TableCell align="right">{p.grossSalary.toFixed(2)}</TableCell>
                                        <TableCell align="right">{p.totalDeductions.toFixed(2)}</TableCell>
                                        <TableCell align="right">
                                            {p.unpaidLeaveDeduction > 0 ? `${p.unpaidLeaveDeduction.toFixed(2)} (${p.unpaidLeaveDays}d)` : '-'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {p.absenceDeduction > 0 ? `${p.absenceDeduction.toFixed(2)} (${p.absentDays}d)` : '-'}
                                        </TableCell>
                                        <TableCell align="right">{p.netSalary.toFixed(2)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={t(`payrollStatus.${p.status}`)}
                                                color={p.status === 'FINALIZED' ? 'success' : 'warning'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={() => navigate(`/payroll/run/${p.id}`)} size="small" aria-label={t('payrollList.show')}>
                                                <VisibilityIcon />
                                            </IconButton>
                                            <IconButton onClick={() => handleDownload(p.id)} size="small" aria-label={t('payrollList.download')}>
                                                <DownloadIcon />
                                            </IconButton>
                                            {p.status === 'DRAFT' && (
                                                <IconButton onClick={() => handleFinalize(p.id)} size="small" color="success" aria-label={t('payrollList.finalize')}>
                                                    <CheckCircleIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {payrolls.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={10} align="center">
                                        {t('payrollList.empty', { month: t(`common:months.${month as MonthNumber}`), year })}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <BatchPaymentDialog
                open={batchDialogOpen}
                onClose={() => setBatchDialogOpen(false)}
                selectedPayrolls={selectedBatchRows}
                onSuccess={handleBatchSuccess}
            />
        </Box>
    );
};

export default PayrollList;
